import { ArrowLeft, CheckCircle, SkipForward, TrendingUp } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import DialogChatAPI from "../../api/dialogChatApi";
import SpecialistApi from "../../api/specialistsApi";
import UsersApi from "../../api/usersApi";
import Loader from "../../components/Loader/Loader";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../components/MultipleSelect/MultipleSelect";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { AvailableLangs } from "../../constans/languagesList";
import { ContextProvider } from "../../contextProvider";
import {
  ProfileFillPercentageResponse,
  ProviderEvidenceFillnessField,
  TypeSpecialistFilter,
} from "../../types/TypeSpecialist";
import { TypeUserData } from "../../types/TypeUsers";
import { getTranslation } from "../../utils/getTranslation";
import { FieldReviewState } from "../../utils/profileDiffUtils";
import Notify, { SuccesNotify } from "../../utils/toaster";
import { PickerData } from "../Companies/SelectedCompany/CompanyPrograms/ProgramInfo/InviteBarterUsers/InviteBarterUsers";
import Profile from "../Profile/Profile";
import { SpecialistReviewPanel } from "../Specialists/components/SpecialistReviewPanel";
import { ReviewMessageModal } from "./components/ReviewMessageModal";
import { ProfileDiffView } from "./ProfileDiffView";
import s from "./ReviewSpecialists.module.css";

interface ReviewStats {
  totalPending: number;
  reviewedToday: number;
  averageReviewTime: number;
  queuePosition: number;
}

type MissingProfileField = {
  key: string;
  label: string;
  details?: string;
};

const providerEvidenceBlockLabels: Record<
  ProviderEvidenceFillnessField["block"],
  string
> = {
  educationCertificates: "Education certificates",
  qualificationCertificates: "Qualification certificates",
  federationCertificates: "Federation certificates",
  workExperience: "Work experience",
  providerCredentials: "Provider credentials",
  providerProfessionalAttributes: "Professional attributes",
  moderatorOnlyProviderEvidence: "Moderator-only provider evidence",
};

const formatProfileFieldLabel = (field: string) =>
  field
    .replace(/\[(\d+)\]/g, " $1")
    .replace(/[._-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const formatProviderEvidenceField = (
  field: ProviderEvidenceFillnessField,
): MissingProfileField => {
  const blockLabel = providerEvidenceBlockLabels[field.block] ?? field.block;
  const fieldLabel = formatProfileFieldLabel(field.field);
  const details = [
    field.index !== undefined ? `item ${field.index + 1}` : undefined,
    field.language ? `language: ${field.language}` : undefined,
    field.reason ? `reason: ${field.reason}` : undefined,
  ]
    .filter(Boolean)
    .join(", ");

  return {
    key: `${field.block}-${field.field}-${field.index ?? "none"}-${
      field.language ?? "all"
    }-${field.reason}`,
    label: `${blockLabel}: ${fieldLabel}`,
    details,
  };
};

const getMissingProfileFields = (
  profileFillness?: ProfileFillPercentageResponse,
): MissingProfileField[] => {
  if (!profileFillness) return [];

  const missingFields =
    profileFillness.incorrectFields?.map((field) => ({
      key: `field-${field}`,
      label: formatProfileFieldLabel(field),
    })) ?? [];

  const missingLanguageFields =
    profileFillness.incorrectLanguageFields?.map((field) => ({
      key: `language-${field.language}-${field.field}`,
      label: formatProfileFieldLabel(field.field),
      details: `language: ${field.language}`,
    })) ?? [];

  const missingProviderEvidenceFields =
    profileFillness.incorrectProviderEvidenceFields?.map(
      formatProviderEvidenceField,
    ) ?? [];

  return [
    ...missingFields,
    ...missingLanguageFields,
    ...missingProviderEvidenceFields,
  ];
};

const ReviewSpecialists = () => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);

  // Queue state
  const [reviewQueue, setReviewQueue] = useState<TypeUserData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [avatars, setAvatars] = useState<{ [userId: string]: string }>({});

  // Stats state
  const [stats, setStats] = useState<ReviewStats>({
    totalPending: 0,
    reviewedToday: 0,
    averageReviewTime: 0,
    queuePosition: 0,
  });
  const [sessionStartTime] = useState(Date.now());
  const [reviewTimes, setReviewTimes] = useState<number[]>([]);
  const [currentReviewStart, setCurrentReviewStart] = useState<number>(
    Date.now()
  );

  // Modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isUpdatedProfileModalOpen, setIsUpdatedProfileModalOpen] =
    useState(false);
  const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
  const [isApprovalMessageModalOpen, setIsApprovalMessageModalOpen] =
    useState(false);
  const [isRejectionMessageModalOpen, setIsRejectionMessageModalOpen] =
    useState(false);
  const [missingProfileFields, setMissingProfileFields] = useState<
    MissingProfileField[]
  >([]);

  // Field-level review tracking
  const [fieldReviews, setFieldReviews] = useState<
    Map<string, FieldReviewState>
  >(new Map());
  const [shortcutsCollapsed, setShortcutsCollapsed] = useState(false);
  const [filterParams, setFilterParams] = useState({
    mainSpecializations: [] as PickerData[],
  });
  const [filters, setFilters] = useState<TypeSpecialistFilter>({
    specialistId: { reviewRequested: true },
  });

  // Current specialist data
  const currentSpecialist = reviewQueue[currentIndex];

  // CRITICAL FIX: Handle ALL specializations, not just [0]
  // Each specialization can be reviewed independently by different managers
  const [selectedSpecializationIndex, setSelectedSpecializationIndex] =
    useState(0);
  const currentSpecializations = currentSpecialist?.specialistIds || [];
  const currentSpecialistInfo =
    currentSpecializations[selectedSpecializationIndex];

  // Load review queue on mount
  useEffect(() => {
    loadReviewQueue();
  }, [filters]);

  // Load avatars when queue changes
  useEffect(() => {
    const loadAvatars = async () => {
      if (token && reviewQueue.length > 0) {
        // Load avatars for next 5 specialists for smoother experience
        const specialistsToLoad = reviewQueue.slice(
          currentIndex,
          Math.min(currentIndex + 5, reviewQueue.length)
        );

        const promises = specialistsToLoad.map((spec) =>
          UsersApi.getUserPublicAvatarAndName(spec._id)
        );
        const responses = await Promise.all(promises);
        const avatarsData = responses.reduce((acc, response) => {
          const avatar = response.avatarS3 || response.avatar;
          if (avatar) {
            acc[response._id] = avatar;
          }
          return acc;
        }, {} as { [userId: string]: string });

        setAvatars((prev) => ({ ...prev, ...avatarsData }));
      }
    };
    loadAvatars();
  }, [reviewQueue, currentIndex, token]);

  // Update stats when queue or index changes
  useEffect(() => {
    setStats((prev) => ({
      ...prev,
      totalPending: reviewQueue.length - currentIndex,
      queuePosition: currentIndex + 1,
    }));
  }, [reviewQueue, currentIndex]);

  // Reset review start time and specialization index when moving to next specialist
  useEffect(() => {
    setCurrentReviewStart(Date.now());
    const needReviewSpecIndex = currentSpecializations.findIndex(
      (spec) =>
        !spec.isVerified || !spec.isVerifiedChanges || spec.reviewRequested
    );
    setSelectedSpecializationIndex(
      needReviewSpecIndex === -1 ? 0 : needReviewSpecIndex
    ); // Reset to first specialization
    setFieldReviews(new Map()); // Clear field reviews when moving to next specialist
    setTotalChangesCount(0); // Reset changes count
  }, [currentIndex]);

  // Also clear field reviews when switching specialization tabs
  useEffect(() => {
    setFieldReviews(new Map());
    setTotalChangesCount(0);
  }, [selectedSpecializationIndex]);

  const loadReviewQueue = async () => {
    if (!token || !userData) return;

    setIsLoader(true);
    try {
      // Get specialists that need review, sorted by oldest first
      // We'll load unverified and those requesting review separately and combine them
      const payload = {
        filters: {
          ...filters,
          page: 1,
          limit: 100, // Load a batch of specialists
          sort: {
            field: "reviewRequestedAt",
            type: 1 as 1,
          },
        },
      };

      const [usersRes, paramsRes] = await Promise.all([
        SpecialistApi.getFilteredSpecialists(token, payload),

        SpecialistApi.getFilterAvailableParams(token, {
          filters: payload.filters,
        }),
      ]);
      if (paramsRes.status && paramsRes.paramsAvailable) {
        const p = paramsRes.paramsAvailable;

        setFilterParams({
          mainSpecializations:
            p.mainSpecializations?.map((i) => ({
              value: i.mainSpecialization.specialization,
              lable: getTranslation(i.mainSpecialization.labels),
              disabled: !i.choosable,
            })) ?? [],
        });
      }
      if (usersRes.status && usersRes.users) {
        // Filter to only include specialists that actually need review
        const needsReview = usersRes.users.filter((user, index) => {
          if (!user.specialistIds?.length) return false;
          const needReviewSpecIndex = user.specialistIds.findIndex(
            (spec) =>
              !spec.isVerified ||
              !spec.isVerifiedChanges ||
              spec.reviewRequested
          );

          if (needReviewSpecIndex !== -1 && index === 0)
            setSelectedSpecializationIndex(needReviewSpecIndex);
          return needReviewSpecIndex !== -1;
        });

        setReviewQueue(needsReview);
        setStats((prev) => ({
          ...prev,
          totalPending: needsReview.length,
          queuePosition: 1,
        }));
      } else {
        setReviewQueue([]);
      }
    } catch (error) {
      console.error("Error loading review queue:", error);
      Notify("Failed to load review queue");
    } finally {
      setIsLoader(false);
    }
  };

  const recordReviewTime = () => {
    const reviewTime = Date.now() - currentReviewStart;
    const newReviewTimes = [...reviewTimes, reviewTime];
    setReviewTimes(newReviewTimes);

    // Calculate average review time
    const avgTime =
      newReviewTimes.reduce((a, b) => a + b, 0) / newReviewTimes.length;
    setStats((prev) => ({
      ...prev,
      averageReviewTime: Math.round(avgTime / 1000), // Convert to seconds
      reviewedToday: prev.reviewedToday + 1,
    }));
  };

  // Track total changes count to know when all fields are reviewed
  const [totalChangesCount, setTotalChangesCount] = useState(0);

  // Handle field review state changes from ProfileDiffView
  const handleFieldReviewChange = (
    fieldName: string,
    state: FieldReviewState
  ) => {
    setFieldReviews((prev) => {
      const newMap = new Map(prev);
      newMap.set(fieldName, state);

      // Check if all fields have been reviewed
      if (newMap.size === totalChangesCount && totalChangesCount > 0) {
        // Check if any fields were rejected
        const hasRejections = Array.from(newMap.values()).some(
          (s) => s.status === "rejected"
        );

        // Auto-open appropriate modal after a short delay to let UI update
        setTimeout(() => {
          // Close diff modal first
          setIsDiffModalOpen(false);

          // Then open the appropriate message modal
          if (hasRejections) {
            setIsRejectionMessageModalOpen(true);
          } else {
            setIsApprovalMessageModalOpen(true);
          }
        }, 300);
      }

      return newMap;
    });
  };

  // Format rejected fields into a bulleted list for rejection message
  const formatRejectedFields = (): string | undefined => {
    const rejected = Array.from(fieldReviews.entries())
      .filter(([_, state]) => state.status === "rejected")
      .map(
        ([fieldName, state]) =>
          `• ${fieldName}: ${state.rejectionReason || "No reason provided"}`
      );

    return rejected.length > 0 ? rejected.join("\n") : undefined;
  };

  // Helper to get specialist name from multi-language array
  const getSpecialistName = (): string => {
    if (!currentSpecialist) return "Specialist";

    const firstName =
      Array.isArray(currentSpecialist.name) && currentSpecialist.name.length > 0
        ? currentSpecialist.name[0].text
        : "";
    const lastName =
      Array.isArray(currentSpecialist.surname) &&
      currentSpecialist.surname.length > 0
        ? currentSpecialist.surname[0].text
        : "";

    return `${firstName} ${lastName}`.trim() || "Specialist";
  };

  // Open message modals instead of direct approve/reject
  const handleApproveClick = async () => {
    setIsApprovalMessageModalOpen(true);
  };

  const handleRejectClick = async () => {
    setIsRejectionMessageModalOpen(true);
  };

  // Actual approve with message
  const handleApproveWithMessage = async (message: string) => {
    if (!token || !currentSpecialistInfo || !userData) return;

    try {
      const response = await SpecialistApi.update(
        {
          _id: currentSpecialistInfo._id,
          isVerified: true,
          isVerifiedChanges: true,
          reviewRequested: false,
        },
        token
      );

      const missingFields = getMissingProfileFields(response.profileFillness);
      if (missingFields.length > 0) {
        setMissingProfileFields(missingFields);
        return;
      }

      if (response.status) {
        await sendAdminMessage(message);

        recordReviewTime();
        setIsApprovalMessageModalOpen(false);
        SuccesNotify("Specialist approved and notified successfully");
        const currentUser = reviewQueue.find((_, idx) => idx === currentIndex);
        const nonApprovedSpecializations =
          currentUser &&
          currentUser.specialistIds!.filter(
            (el) => !el.isVerifiedChanges && el.reviewRequested
          );

        if (
          !nonApprovedSpecializations ||
          nonApprovedSpecializations.length < 2
        ) {
          if (currentIndex === reviewQueue.length - 1) {
            handleSkip();
          }

          // Remove approved specialist from queue
          setReviewQueue((prev) =>
            prev.filter((_, idx) => idx !== currentIndex)
          );
        } else {
          setReviewQueue((prev) =>
            prev.map((item, idx) => {
              if (idx === currentIndex) {
                return {
                  ...item,
                  specialistIds: item.specialistIds!.map((spec) => {
                    if (spec._id === currentSpecialistInfo._id) {
                      return {
                        ...spec,
                        isVerifiedChanges: true,
                        reviewRequested: false,
                      };
                    } else return spec;
                  }),
                };
              } else return item;
            })
          );
        }

        // Don't increment index since we removed the current item
        // The next specialist will automatically be at currentIndex
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error approving specialist:", error);
      Notify("Failed to approve specialist");
    }
  };

  // Actual reject with message
  const handleRejectWithMessage = async (message: string) => {
    if (!token || !currentSpecialistInfo || !userData) return;

    try {
      // 1. Send admin message to specialist
      await sendAdminMessage(message);

      // 2. Update specialist status
      const response = await SpecialistApi.update(
        {
          _id: currentSpecialistInfo._id,
          isVerifiedChanges: false,
          reviewRequested: false,
        },
        token
      );

      if (response.status) {
        recordReviewTime();
        setIsRejectionMessageModalOpen(false);
        SuccesNotify("Specialist rejected and notified with feedback");

        // Remove rejected specialist from queue
        setReviewQueue((prev) => prev.filter((_, idx) => idx !== currentIndex));

        // Don't increment index since we removed the current item
        // The next specialist will automatically be at currentIndex
      } else if (response.message) {
        Notify(response.message);
      }
    } catch (error) {
      console.error("Error rejecting specialist:", error);
      Notify("Failed to reject specialist");
    }
  };

  // Helper to send admin message
  const sendAdminMessage = async (message: string) => {
    if (!token || !userData || !currentSpecialist) return;

    // First, create or get dialog chat with the specialist
    const chatResponse = await DialogChatAPI.createDialogChat(token, {
      userId: userData._id,
      userIdToSend: currentSpecialist._id,
    });

    if (!chatResponse.status) {
      throw new Error("Failed to create chat");
    }

    // Then send the message via socket
    await DialogChatAPI.sendMessage({
      userIdToSend: currentSpecialist._id,
      message: message,
      userId: userData._id,
      date: new Date(),
      files: [],
    });
  };

  const handleSkip = () => {
    moveToNext();
  };

  const moveToNext = () => {
    if (currentIndex < reviewQueue.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      // Reached end of queue, reload to check for new specialists
      loadReviewQueue();
      setCurrentIndex(0);
    }
  };

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  if (isLoader) {
    return (
      <div className={s.container}>
        <div className={s.loaderContainer}>
          <Loader size={80} />
          <p className={s.loadingText}>Loading review queue...</p>
        </div>
      </div>
    );
  }

  if (reviewQueue.length === 0) {
    return (
      <div className={s.container}>
        <div className={s.emptyState}>
          <CheckCircle className={s.emptyIcon} size={64} />
          <h2 className={s.emptyTitle}>All caught up! 🎉</h2>
          <p className={s.emptyText}>
            There are no specialists waiting for review at the moment.
          </p>
          {stats.reviewedToday > 0 && (
            <Card className={s.statsCard}>
              <CardContent className="pt-6">
                <div className={s.sessionStats}>
                  <div className={s.statItem}>
                    <span className={s.statLabel}>Reviewed Today</span>
                    <span className={s.statValue}>{stats.reviewedToday}</span>
                  </div>
                  {stats.averageReviewTime > 0 && (
                    <div className={s.statItem}>
                      <span className={s.statLabel}>Avg Review Time</span>
                      <span className={s.statValue}>
                        {formatTime(stats.averageReviewTime)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Button
            onClick={() => {
              setFilters((p) => ({
                ...p,
                specialistId: {
                  ...p.specialistId,
                  mainSpecializations: undefined,
                },
              }));
              loadReviewQueue();
            }}
            className="mt-4"
          >
            Refresh Queue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      {/* Header with Stats */}
      <div className={s.header}>
        <div className={s.headerTop}>
          <div className={s.titleBlock}>
            <h1 className={s.title}>Review Queue</h1>
            <Badge variant="destructive" className={s.pendingBadge}>
              {stats.totalPending} Pending
            </Badge>
            <MultipleSelect
              data={[
                ...filterParams.mainSpecializations,
                { lable: "All specializations", value: "initial" },
              ]}
              value={filters.specialistId?.mainSpecializations ?? ["initial"]}
              setValue={(v) =>
                setFilters((p) => ({
                  ...p,
                  specialistId: {
                    ...p.specialistId,
                    mainSpecializations:
                      v && v !== "initial" ? [v as string] : undefined,
                  },
                }))
              }
              lable="Specialization"
              multiplie={false}
              width="200px"
            />
          </div>
          <Button variant="outline" onClick={loadReviewQueue} size="sm">
            Refresh Queue
          </Button>
        </div>

        {/* Stats Cards */}
        <div className={s.statsGrid}>
          <Card className={s.statCard}>
            <CardContent className="pt-4">
              <div className={s.statContent}>
                <div className={s.statIcon}>
                  <TrendingUp size={20} />
                </div>
                <div>
                  <p className={s.statLabel}>Queue Position</p>
                  <p className={s.statValue}>
                    {stats.queuePosition} / {reviewQueue.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={s.statCard}>
            <CardContent className="pt-4">
              <div className={s.statContent}>
                <div className={s.statIcon}>
                  <CheckCircle size={20} />
                </div>
                <div>
                  <p className={s.statLabel}>Reviewed Today</p>
                  <p className={s.statValue}>{stats.reviewedToday}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {stats.averageReviewTime > 0 && (
            <Card className={s.statCard}>
              <CardContent className="pt-4">
                <div className={s.statContent}>
                  <div className={s.statIcon}>⏱️</div>
                  <div>
                    <p className={s.statLabel}>Avg Review Time</p>
                    <p className={s.statValue}>
                      {formatTime(stats.averageReviewTime)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Progress Bar */}
        <div className={s.progressContainer}>
          <div className={s.progressBar}>
            <div
              className={s.progressFill}
              style={{
                width: `${(currentIndex / reviewQueue.length) * 100}%`,
              }}
            />
          </div>
          <p className={s.progressText}>
            {Math.round((currentIndex / reviewQueue.length) * 100)}% Complete
          </p>
        </div>
      </div>

      {/* Main Review Area */}
      <div className={s.reviewArea}>
        {/* Navigation Controls */}
        <div className={s.navigationControls}>
          <Button
            variant="outline"
            size="sm"
            onClick={moveToPrevious}
            disabled={currentIndex === 0}
            className={s.navButton}
          >
            <ArrowLeft size={16} />
            Previous
          </Button>

          <div className={s.queueInfo}>
            <span className={s.queueText}>
              Reviewing {currentIndex + 1} of {reviewQueue.length}
            </span>
            {currentIndex < reviewQueue.length - 1 && (
              <span className={s.nextInfo}>
                {`Next: ${getTranslation(
                  reviewQueue[currentIndex + 1]?.name,
                  "en"
                )} ${getTranslation(
                  reviewQueue[currentIndex + 1]?.surname,
                  "en"
                )}`}
              </span>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSkip}
            className={s.navButton}
          >
            Skip
            <SkipForward size={16} />
          </Button>
        </div>

        {/* Multiple Specializations Selector */}
        {currentSpecializations.length > 1 && (
          <Card className="mb-4 bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-sm text-white">
                Multiple Specializations Found ({currentSpecializations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {currentSpecializations.map((spec, idx) => {
                  const specializationName =
                    spec.mainSpecializations?.[0]?.specialization ||
                    `Specialization ${idx + 1}`;
                  return (
                    <Button
                      key={spec._id}
                      variant={
                        selectedSpecializationIndex === idx
                          ? "outline"
                          : "default"
                      }
                      size="sm"
                      onClick={() => setSelectedSpecializationIndex(idx)}
                      className="text-xs"
                    >
                      {specializationName}
                      {spec.reviewRequested && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Needs Review
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Note: Each specialization profile is reviewed separately
              </p>
            </CardContent>
          </Card>
        )}

        {/* Review Panel */}
        {currentSpecialist && currentSpecialistInfo && (
          <div className={s.reviewPanel}>
            <SpecialistReviewPanel
              specialist={currentSpecialist}
              specialistInfo={currentSpecialistInfo}
              avatar={avatars[currentSpecialist._id]}
              onApprove={handleApproveClick}
              onReject={handleRejectClick}
              onViewProfile={(isPublic) => {
                if (isPublic) {
                  setIsProfileModalOpen(true);
                } else {
                  setIsUpdatedProfileModalOpen(true);
                }
              }}
              onViewDiff={() => setIsDiffModalOpen(true)}
              onClose={() => {
                // In review mode, closing goes to next specialist
                moveToNext();
              }}
            />
          </div>
        )}

        {/* Quick Action Reminder */}
        {shortcutsCollapsed && (
          <button
            className={s.expandBtn}
            onClick={() => setShortcutsCollapsed(false)}
          >
            ⌨️
          </button>
        )}
        {!shortcutsCollapsed && (
          <div className={s.shortcutsWrapper}>
            <Card className={s.shortcutsCard}>
              <CardHeader className={s.shortcutHeader}>
                <CardTitle className="text-sm text-white">
                  Keyboard Shortcuts
                </CardTitle>
                <button
                  className={s.collapseBtn}
                  onClick={() => setShortcutsCollapsed(true)}
                >
                  ✕
                </button>
              </CardHeader>

              <CardContent>
                <div className={s.shortcutsList}>
                  <div className={s.shortcutItem}>
                    <kbd className={s.kbd}>Alt</kbd> +{" "}
                    <kbd className={s.kbd}>A</kbd>
                    <span>Approve</span>
                  </div>
                  <div className={s.shortcutItem}>
                    <kbd className={s.kbd}>Alt</kbd> +{" "}
                    <kbd className={s.kbd}>R</kbd>
                    <span>Reject</span>
                  </div>
                  <div className={s.shortcutItem}>
                    <kbd className={s.kbd}>Ctrl</kbd> +{" "}
                    <kbd className={s.kbd}>D</kbd>
                    <span>View Changes</span>
                  </div>
                  <div className={s.shortcutItem}>
                    <kbd className={s.kbd}>Esc</kbd>
                    <span>Next/Close</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Modals */}
      <ModalWindow
        isOpen={isProfileModalOpen}
        setIsOpen={setIsProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0 20px"
      >
        {currentSpecialist && (
          <Profile
            userId={currentSpecialist._id}
            isOpen={isProfileModalOpen}
            setIsOpen={setIsProfileModalOpen}
            getPublicInfoString={`${currentSpecialist.latinName}-${
              currentSpecialist.latinSurname
            }-${currentSpecialist._id.slice(-7)}`}
            isPublicProfile
          />
        )}
      </ModalWindow>

      <ModalWindow
        isOpen={isUpdatedProfileModalOpen}
        setIsOpen={setIsUpdatedProfileModalOpen}
        height="90vh"
        width="90vw"
        padding="0 20px"
      >
        {currentSpecialist && (
          <Profile
            userId={currentSpecialist._id}
            isOpen={isUpdatedProfileModalOpen}
            setIsOpen={setIsUpdatedProfileModalOpen}
            getPublicInfoString={`${currentSpecialist.latinName}-${
              currentSpecialist.latinSurname
            }-${currentSpecialist._id.slice(-7)}`}
            isPublicProfile={false}
            initialSpecialization={currentSpecialistInfo?._id}
          />
        )}
      </ModalWindow>

      <ModalWindow
        isOpen={isDiffModalOpen}
        setIsOpen={setIsDiffModalOpen}
        height="90vh"
        width="90vw"
        padding="0"
      >
        {currentSpecialist && currentSpecialistInfo && (
          <ProfileDiffView
            userId={currentSpecialist._id}
            currentUser={currentSpecialist}
            currentSpecialistInfo={currentSpecialistInfo}
            specializationLabel={
              currentSpecializations.length > 1
                ? currentSpecialistInfo.mainSpecializations?.[0]
                    ?.specialization || "Unknown"
                : undefined
            }
            onFieldReviewChange={handleFieldReviewChange}
            onChangesLoaded={setTotalChangesCount}
          />
        )}
      </ModalWindow>

      {/* Missing Required Profile Fields Modal */}
      <ModalWindow
        isOpen={missingProfileFields.length > 0}
        setIsOpen={() => setMissingProfileFields([])}
        height="fit-content"
        width="min(560px, 90vw)"
        padding="24px"
        title="Profile approval blocked"
      >
        <div className={s.missingFieldsModal}>
          <p className={s.missingFieldsText}>
            This specialist profile cannot be approved until these required
            fields are filled:
          </p>
          <div className={s.missingFieldsList}>
            {missingProfileFields.map((field) => (
              <div key={field.key} className={s.missingFieldItem}>
                <span className={s.missingFieldLabel}>{field.label}</span>
                {field.details && (
                  <span className={s.missingFieldDetails}>
                    {field.details}
                  </span>
                )}
              </div>
            ))}
          </div>
          <Button
            className={s.missingFieldsButton}
            onClick={() => setMissingProfileFields([])}
          >
            Got it
          </Button>
        </div>
      </ModalWindow>

      {/* Approval Message Modal */}
      <ModalWindow
        isOpen={isApprovalMessageModalOpen}
        setIsOpen={setIsApprovalMessageModalOpen}
        height="fit-content"
        width="fit-content"
        padding="20px"
        bgColor="transparent"
      >
        {currentSpecialist && currentSpecialistInfo && (
          <ReviewMessageModal
            isApproval={true}
            specialistName={getSpecialistName()}
            specializationLabel={
              currentSpecializations.length > 1
                ? currentSpecialistInfo.mainSpecializations?.[0]?.specialization
                : undefined
            }
            onSend={handleApproveWithMessage}
            onCancel={() => setIsApprovalMessageModalOpen(false)}
            userSelectedLang={
              currentSpecialist.selectedLanguage as unknown as AvailableLangs
            }
          />
        )}
      </ModalWindow>

      {/* Rejection Message Modal */}
      <ModalWindow
        isOpen={isRejectionMessageModalOpen}
        setIsOpen={setIsRejectionMessageModalOpen}
        height="fit-content"
        width="fit-content"
        padding="20px"
        bgColor="transparent"
      >
        {currentSpecialist && currentSpecialistInfo && (
          <ReviewMessageModal
            isApproval={false}
            specialistName={getSpecialistName()}
            specializationLabel={
              currentSpecializations.length > 1
                ? currentSpecialistInfo.mainSpecializations?.[0]?.specialization
                : undefined
            }
            rejectedFields={formatRejectedFields()}
            onSend={handleRejectWithMessage}
            onCancel={() => setIsRejectionMessageModalOpen(false)}
            userSelectedLang={
              currentSpecialist.selectedLanguage as unknown as AvailableLangs
            }
          />
        )}
      </ModalWindow>
    </div>
  );
};

export default ReviewSpecialists;
